import React from "react";
import ReactCountryFlag from "react-country-flag";
import { cn } from "../utils/cn";

export interface CountryFlagProps {
  /** Country code (e.g., 'MX', 'RD', 'CO', 'CL', 'PE') */
  countryCode: string;
  /** Size of the flag in pixels or em */
  size?: number | string;
  /** Additional CSS classes */
  className?: string;
  /** Title/tooltip text */
  title?: string;
  /** Whether to show as SVG (default: true) */
  svg?: boolean;
}

/**
 * Maps internal country codes to ISO 3166-1 alpha-2 codes for react-country-flag
 */
const COUNTRY_CODE_MAP: Record<string, string> = {
  MX: "mx",
  RD: "do", // República Dominicana
  CO: "co",
  CL: "cl",
  PE: "pe",
};

/**
 * Convert internal country code to ISO format for react-country-flag
 */
const getISOCountryCode = (code: string): string => {
  return COUNTRY_CODE_MAP[code.toUpperCase()] || code.toLowerCase();
};

/**
 * CountryFlag - Centralized component for displaying country flags
 *
 * @example
 * ```tsx
 * <CountryFlag countryCode="MX" size={24} />
 * <CountryFlag countryCode="RD" size="1.5em" className="mr-2" />
 * ```
 */
export const CountryFlag: React.FC<CountryFlagProps> = ({
  countryCode,
  size = "1.2em",
  className,
  title,
  svg = true,
}) => {
  const isoCode = getISOCountryCode(countryCode);

  if (!isoCode) {
    return null;
  }

  const sizeValue = typeof size === "number" ? `${size}px` : size;

  return (
    <ReactCountryFlag
      countryCode={isoCode}
      svg={svg}
      style={{
        width: sizeValue,
        height: sizeValue,
      }}
      className={cn("inline-block", className)}
      title={title}
    />
  );
};

CountryFlag.displayName = "CountryFlag";
